import React, { useRef, useState } from 'react'
import { trackPromise } from 'react-promise-tracker'
import { toast } from 'react-toastify'
import { ApiError } from 'services/ApiService'
import { Form } from '@unform/web'
import { FormHandles, SubmitHandler } from '@unform/core'
import UserChatService from 'services/UserChatService'
import { handleFormValidationErrors } from 'lib/validation/form'
import { ErrorTypes } from 'lib/constants/ErrorTypes'
import { useRouter } from 'next/router'
import UserService from 'services/UserService'
import { User } from 'interfaces/User'
import { useTranslation } from 'next-i18next'
import { chatRoute } from 'lib/constants/routes'
import FeatureDialog from 'stories/elements/Modal/DialogWindow/FeatureDialog'
import NewButton from 'components/elements/NewButton'
import theme from 'stories/utils/theme'
import { Body2, ButtonText } from 'stories/elements/Typography/Text'
import { ChatType, UserChat } from 'interfaces/UserChat'
import { trackEvent } from 'lib/tracking/analytics-service'
import _useTranslation from 'lib/hooks/_useTranslation'
import { AnalyticsEventChatSend } from 'lib/constants/AnalyticsEventType'
import Input from 'components/elements/Inputs/Input'
import Textarea from 'components/elements/Inputs/Textarea'
import styled, { css } from 'styled-components'

const Tabs = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    width: 100%;
    color: ${theme.colors.white};
    p {
        text-align: center;
        color: ${theme.colors.accentBlue};
        padding: 10px;
    }
`

const Tab = styled.div<{ isSelected?: boolean; activeColor?: string; secondaryTabColor?: string }>`
    cursor: pointer;
    ${({ isSelected }) =>
        isSelected
            ? css`
                  border-top: 1px solid ${theme.colors.blueIce};
                  border-right: 1px solid ${theme.colors.blueIce};
                  border-left: 1px solid ${theme.colors.blueIce};
                  border-radius: ${theme.borderRadius.normal} ${theme.borderRadius.normal} 0 0;
                  p {
                      color: ${theme.colors.deepSeaBlue};
                      font-weight: 700;
                  }
              `
            : css`
                  border-bottom: 1px solid ${theme.colors.blueIce};
              `}
`

const loadingAreas = {
    create: 'createTutorChat',
}

interface Chat1on1FormProps {
    username: string
    message: string
}

interface GroupChatFormProps {
    groupChatUuid: string
}

interface Props {
    user?: User
    open: boolean
    handleClose: () => void
    selectChat?: (chat: UserChat | undefined) => void
}

const CreateNewChatDialog = ({ open, handleClose, user, selectChat }: Props) => {
    const [selectedTabIndex, setSelectedTabIndex] = useState<ChatType>(ChatType.OneOnOne)
    const new1on1ChatFormRef = useRef<FormHandles>(null)
    const newGroupChatFormRef = useRef<FormHandles>(null)
    const router = useRouter()
    const { t: tCommon } = useTranslation('common')
    const { t } = _useTranslation('chat')

    const createChat = (userUuid: string, message: string) => {
        UserChatService.getChatByUser(userUuid)
            .then((data) => {
                UserChatService.postMessage(data.chat.uuid, {
                    message,
                    knowUuid: null,
                    attachmentPath: null,
                })
                    .then((chatMessage) => {
                        trackEvent(AnalyticsEventChatSend, {
                            value: data.chat.uuid,
                            chat: data.chat,
                            chatMessage,
                            chatPartnerUuid: data.chat.chatPartner?.uuid,
                        })
                        if (data.isNew) {
                            toast.success(t('chat/newChatCreated'))
                            selectChat && selectChat(data.chat)
                        }
                        handleClose()
                        router.push(chatRoute)
                    })
                    .catch((error) => {
                        if (error instanceof ApiError) {
                            error.handleUnknownError(tCommon, 'userChat.postMessage')
                        } else {
                            throw error
                        }
                    })
            })
            .catch((error) => {
                if (error instanceof ApiError) {
                    if (error.type === ErrorTypes.FormValidation) {
                        handleFormValidationErrors(tCommon, error, new1on1ChatFormRef.current!)
                    } else {
                        error.handleUnknownError(tCommon, 'user.getChatByUser')
                    }
                } else {
                    throw error
                }
            })
    }

    const handleJoin1on1ChatSubmit: SubmitHandler<Chat1on1FormProps> = (data) => {
        new1on1ChatFormRef.current!.setErrors({})

        if (user?.uuid) {
            createChat(user.uuid, data.message)
        } else {
            trackPromise(
                UserService.getUserByUsername(data.username)
                    .then((user) => {
                        createChat(user.uuid, data.message)
                    })
                    .catch((error) => {
                        if (error instanceof ApiError) {
                            if (error.type === ErrorTypes.NotFound) {
                                toast.error(t('chat/newChatUsernameNotFound'))
                            } else {
                                error.handleUnknownError(tCommon, 'userChat.getUserByUsername')
                            }
                        } else {
                            throw error
                        }
                    }),
                loadingAreas.create
            )
        }
    }

    const handleJoinGroupChatSubmit: SubmitHandler<GroupChatFormProps> = ({ groupChatUuid }) => {
        newGroupChatFormRef.current!.setErrors({})

        if (!groupChatUuid) {
            return
        }

        trackPromise(
            UserChatService.joinGroupChat(groupChatUuid)
                .then(() => {
                    handleClose()
                    router.push(chatRoute)
                })
                .catch((error) => {
                    if (error instanceof ApiError) {
                        error.handleUnknownError(tCommon, 'userChat.joinGroupChat')
                    } else {
                        throw error
                    }
                }),
            loadingAreas.create
        )
    }

    return (
        <FeatureDialog
            open={open}
            onClose={handleClose}
            ariaPrefix="new-chat-dialog"
            title={t('chat/newChatFormDialogTitle')}
        >
            <Tabs>
                {[ChatType.OneOnOne, ChatType.Group].map((label) => (
                    <Tab key={label} isSelected={selectedTabIndex === label} onClick={() => setSelectedTabIndex(label)}>
                        <Body2 fontFamily="var(--font-inter)">{label}</Body2>
                    </Tab>
                ))}
            </Tabs>
            {selectedTabIndex === ChatType.OneOnOne ? (
                <Form
                    onSubmit={handleJoin1on1ChatSubmit}
                    ref={new1on1ChatFormRef}
                    initialData={{ username: user?.username ?? '' }}
                >
                    <Input placeholder={t('chat/newChatFormUsernameLabel')} name="username" />
                    <Textarea placeholder={t('chat/newChatFormMessageLabel')} name="message" height={120} />
                    <NewButton
                        fullWidth
                        loadingArea={loadingAreas.create}
                        color={theme.colors.deepSeaBlue}
                        borderRadius={theme.borderRadius.normal}
                    >
                        <ButtonText color={theme.colors.white}>{t('chat/newChatFormMessageSubmitButton')}</ButtonText>
                    </NewButton>
                </Form>
            ) : (
                <Form onSubmit={handleJoinGroupChatSubmit} ref={newGroupChatFormRef}>
                    <Input placeholder={'Group chat uuid'} name="groupChatUuid" />
                    <NewButton
                        fullWidth
                        loadingArea={loadingAreas.create}
                        color={theme.colors.deepSeaBlue}
                        borderRadius={theme.borderRadius.normal}
                    >
                        <ButtonText color={theme.colors.white}>
                            {(selectedTabIndex as ChatType) === ChatType.OneOnOne
                                ? t('chat/newChatFormMessageSubmitButton')
                                : 'Join'}
                        </ButtonText>
                    </NewButton>
                </Form>
            )}
        </FeatureDialog>
    )
}

export default CreateNewChatDialog
